import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { KioskStore } from '../../../kiosk/core/stores/KioskStore';
import { CartItemTypes } from '../enums/CartItemTypes';
import { ICart } from '../interfaces/ICart';
import { ICartAction } from '../interfaces/ICartAction';
import { ICartItem } from '../interfaces/ICartItem';
import { IGroupedShow } from '../interfaces/IGroupedShow';
import { groupCartTicketsByShow } from '../utils/groupCartTicketsByShow';
import { TerminalsStore } from '../../../terminals/core/stores/TerminalsStore';
import { VouchersStore } from '../../../vouchers/core/stores/VouchersStore';

@Injectable({
  providedIn: 'root'
})
export class CartStore {

  public readonly cartAction$ = new Subject<ICartAction>();
  public readonly cart = signal<ICart>(null);
  public readonly cardIdsWithFreeEntries = signal<number[]>([]);
  public readonly customerCards = computed(() => this.cart()?.customerCards || []);

  private readonly _terminalsStore = inject(TerminalsStore);
  private readonly _vouchersStore = inject(VouchersStore);

  public readonly groupedCartItemsTickets = computed<IGroupedShow[]>(() =>
    groupCartTicketsByShow(this.cart()?.items ?? [])
  );

  public readonly showTicketCinemaName = computed(() => {
    return this.cartItemsTickets().some(x => x.cinemaId !== this.cart().cinemaId) || this._terminalsStore.changedCinema();
  });

  public readonly cartItemsProductsAndPackages = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.Product || x.itemType === CartItemTypes.Package) || [];
  });

  public readonly cartItemsTickets = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.Ticket) || [];
  });

  public readonly cartItemsVouchers = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.Voucher) || [];
  });

  public readonly cartItemsExternalVouchers = computed<ICartItem[]>(() => {
    const externalVouchersTypes = this._vouchersStore.externalVoucherTypes();
    return this.cartItemsCashedVouchers().filter(x => externalVouchersTypes.some(y => y.voucherTypeId === x.itemId));
  });

  public readonly cartItemsCancelledTickets = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.CancelTicket) || [];
  });

  public readonly cartItemsCancelledGoods = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(
        x => x.itemType === CartItemTypes.CancelProduct || x.itemType === CartItemTypes.CancelPackage
      ) || [];
  });

  public readonly cartItemsCancelledVouchers = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.CancelVoucher) || [];
  });

  public readonly cartItemsCashedVouchers = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.CashVoucher) || [];
  });

  public readonly cartItemsCards = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x =>
        x.itemType === CartItemTypes.Card ||
        x.itemType === CartItemTypes.LoadCard ||
        x.itemType === CartItemTypes.CancelCard
      ) || [];
  });

  public readonly cartItemsCardPayments = computed<ICartItem[]>(() => {
    return this.cart()?.items
      .filter(x => x.itemType === CartItemTypes.CashCineCard) || [];
  });

  private readonly _kioskStore = inject(KioskStore);

  public constructor() {
    toObservable(this.cart)
      .subscribe(cart => {
        if (cart?.items.length === 0) {
          this._kioskStore.rejectedPackagesToConvertIds.set([]);
          this.cardIdsWithFreeEntries.set([]);
        }
      });
  }
}
